import { useState } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Checkbox, LoadingShim, Row } from "@narmi/design_system";
import { Dialog } from "cerulean";

import Dropzone from "../../receipts/components/UploadReceiptDialog/Dropzone";
import formatFileSize from "../../receipts/formatFileSize";

import PanelCard from "./PanelCard/PanelCard";
import styles from "./ReceiptPanelSection.module.scss";

// The same card renders in the section and inside the remove dialog; the dialog
// copy is read-only, so it drops the actions (Figma details-1.3).
const ReceiptFileCard = ({
  receipt,
  actions,
  testId,
}: {
  receipt: API.TransactionReceipt;
  actions?: React.ReactNode;
  testId: string;
}) => (
  <PanelCard testId={testId}>
    <Row alignItems="center" gapSize="xs">
      <Row.Item shrink>
        <span className={styles["status-icon"]} aria-hidden="true">
          <span className="narmi-icon-check" />
        </span>
      </Row.Item>
      <Row.Item>
        <div className={styles["file-text"]}>
          <span className={styles.filename}>{receipt.original_filename}</span>
          <span className="fontColor--secondary">
            {formatFileSize(receipt.size_bytes)}
          </span>
        </div>
      </Row.Item>
      {actions && <Row.Item shrink>{actions}</Row.Item>}
    </Row>
  </PanelCard>
);

type ReceiptPanelSectionProps = {
  transaction: API.Transaction;
  canWrite: boolean;
  isUploading: boolean;
  acceptMimeTypes?: readonly string[];
  onFileChosen: (transaction: API.Transaction, file: File) => void;
  onRemove: (
    transaction: API.Transaction,
    options: { deleteReceipt: boolean }
  ) => Promise<void>;
};

const ReceiptPanelSection = ({
  transaction,
  canWrite,
  isUploading,
  acceptMimeTypes,
  onFileChosen,
  onRemove,
}: ReceiptPanelSectionProps) => {
  const { l10n } = useLocalization();
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [deleteReceipt, setDeleteReceipt] = useState(false);
  const { receipt } = transaction;

  if (isUploading) {
    return (
      <div className={styles.uploading} data-testid="panel-receipt-uploading">
        <LoadingShim isLoading />
      </div>
    );
  }

  if (!receipt) {
    // A transaction holds one receipt, so the drop zone only exists while it has
    // none. Viewers never get it.
    if (!canWrite) {
      return (
        <p className="fontColor--secondary" data-testid="panel-receipt-empty">
          {l10n.getString("live-ledger-panel-receipt-none")}
        </p>
      );
    }
    return (
      <div data-testid="panel-receipt-dropzone">
        <Dropzone
          onFileSelected={(file) => onFileChosen(transaction, file)}
          acceptMimeTypes={acceptMimeTypes}
          layout="inline"
        />
      </div>
    );
  }

  const handleConfirmRemove = async () => {
    setIsRemoving(true);
    try {
      await onRemove(transaction, { deleteReceipt });
    } finally {
      setIsRemoving(false);
      setIsConfirmingRemove(false);
      setDeleteReceipt(false);
    }
  };

  const closeConfirmation = () => {
    setIsConfirmingRemove(false);
    setDeleteReceipt(false);
  };

  return (
    <div data-testid="panel-receipt-attached">
      <ReceiptFileCard
        receipt={receipt}
        testId="panel-receipt-card"
        actions={
          <div className={styles.actions}>
            <a
              className={styles["icon-button"]}
              href={receipt.download_url ?? undefined}
              download={receipt.original_filename}
              aria-label={l10n.getString("live-ledger-panel-receipt-download")}
              data-testid="panel-receipt-download"
            >
              <span className="narmi-icon-download" aria-hidden="true" />
            </a>
            {canWrite && (
              <button
                type="button"
                className={styles["icon-button"]}
                onClick={() => setIsConfirmingRemove(true)}
                aria-label={l10n.getString("live-ledger-panel-receipt-remove")}
                data-testid="panel-receipt-remove"
              >
                <span className="narmi-icon-x" aria-hidden="true" />
              </button>
            )}
          </div>
        }
      />
      <Dialog
        isOpen={isConfirmingRemove}
        title={l10n.getString("live-ledger-panel-receipt-remove-title")}
        headerStyle="bordered"
        width="580px"
        onUserDismiss={closeConfirmation}
        testId="panel-receipt-remove-dialog"
        footer={
          <Row justifyContent="end" alignItems="center" gapSize="s">
            <Row.Item shrink>
              <Button
                kind="plain"
                startIcon="x"
                label={l10n.getString(
                  "live-ledger-panel-receipt-remove-cancel"
                )}
                onClick={closeConfirmation}
                testId="panel-receipt-remove-cancel"
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                kind="primary"
                label={l10n.getString(
                  "live-ledger-panel-receipt-remove-confirm"
                )}
                onClick={handleConfirmRemove}
                isLoading={isRemoving}
                disabled={isRemoving}
                testId="panel-receipt-remove-confirm"
              />
            </Row.Item>
          </Row>
        }
      >
        <p className="margin--bottom--l">
          {l10n.getString("live-ledger-panel-receipt-remove-body")}
        </p>
        <ReceiptFileCard receipt={receipt} testId="panel-receipt-dialog-card" />
        <div className="margin--top--l">
          <Checkbox
            checked={deleteReceipt}
            onChange={(event) => setDeleteReceipt(event.target.checked)}
            label={l10n.getString("live-ledger-panel-receipt-remove-delete")}
            testId="panel-receipt-remove-delete"
          />
        </div>
      </Dialog>
    </div>
  );
};

export default ReceiptPanelSection;
